import { ApiError, type ProblemDetails } from './types';

/**
 * Minimal duck-typed axios-like error shape. Kept framework-agnostic so this
 * module has no runtime dependency on axios.
 */
interface AxiosLikeError {
  response?: {
    status?: number;
    headers?: Record<string, string | undefined> | { 'content-type'?: string };
    data?: unknown;
  };
}

interface LegacyErrorShape {
  message: string;
  errors?: Record<string, string[]>;
}

function getContentType(headers: unknown): string | null {
  if (!headers || typeof headers !== 'object') return null;
  const record = headers as Record<string, unknown>;
  const direct = record['content-type'] ?? record['Content-Type'];
  return typeof direct === 'string' ? direct : null;
}

function isProblemDetails(data: unknown): data is ProblemDetails {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.type === 'string' &&
    typeof d.title === 'string' &&
    typeof d.status === 'number' &&
    typeof d.code === 'string'
  );
}

function isLegacyErrorShape(data: unknown): data is LegacyErrorShape {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return typeof d.message === 'string' && d.message.length > 0;
}

export function parseProblem(err: unknown): ApiError | null {
  if (!err || typeof err !== 'object') return null;
  const axiosErr = err as AxiosLikeError;
  if (!axiosErr.response) return null;

  const contentType = getContentType(axiosErr.response.headers);
  if (!contentType) return null;

  const ct = contentType.toLowerCase();
  const data = axiosErr.response.data;
  const status = axiosErr.response.status ?? 0;

  if (ct.includes('application/problem+json')) {
    if (!isProblemDetails(data)) {
      console.warn('[api-errors] parseProblem: response has problem+json content-type but invalid shape', data);
      return null;
    }
    return new ApiError(data);
  }

  // Legacy fallback: backend endpoints not yet migrated to ProblemDetails
  // still return `{ message, errors? }` as application/json for 4xx/5xx.
  // Synthesize an ApiError so downstream consumers (toastFromApiError,
  // mapProblemToFormErrors) get a uniform shape. Code is LEGACY_ERROR so
  // resolveMessage falls through to the backend-provided title.
  if (ct.includes('application/json') && status >= 400 && isLegacyErrorShape(data)) {
    return new ApiError({
      type: 'about:blank',
      title: data.message,
      status,
      code: 'LEGACY_ERROR',
      errors: data.errors,
    });
  }

  return null;
}
